import { DisplayPost, isRenderableInFeed } from '../components/DisplayPost';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { LoadingText, HeavyText, LightItalicText } from '../components/Style';
import styled from 'styled-components';
import api from '../api';
import { useAuth } from '../context/auth';
import CTACard from '../components/CTACard';
import { usePromptResponseChooser } from '../components/PromptResponseChooser';
import { BadgeIcon, BADGE_CONFIG, VinylDisc, VINYL_THEMES } from '../components/UserBadge';
import useInfiniteScroll from '../hooks/useInfiniteScroll';
import { trackEvent } from '../analytics';
import PlayAllButton from '../components/PlayAllButton';
import SuggestedFriends from '../components/SuggestedFriends';

// The top-of-feed Lists announcement. Its key changed when the card added 3x3
// (was 'feed_lists'), so people who dismissed the first version see it once more.
var LISTS_CTA_KEY = 'feed_lists_3x3';

// --- Layout ---

var FeedContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1em;
  align-items: center;
  max-width: 410px;
  margin: 0 auto;
  padding: 1em 0.75em 0;

  @media (max-width: 360px) {
    padding: 0.75em 0.35em 0;
  }
`;

var EmptyMessage = styled.p`
  text-align: center;
  color: #999;
  margin-top: 2rem;
  font-size: 0.9rem;
`;

// --- Tab bar + sort + filter ---

var TabRow = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 400px;
  background: white;
  border: 1px solid #ddd;
  border-right-width: 2px;
  border-bottom-width: 2px;
  border-radius: 12px;
  padding: 0.5rem 0.75rem;
  box-sizing: border-box;
  gap: 0.4rem;
`;

var TabList = styled.div`
  display: flex;
  gap: 0;
  flex: 1;
`;

var Tab = styled.button`
  background: none;
  border: none;
  padding: 0.3rem 0.5rem;
  cursor: pointer;
  font-size: 0.85rem;
  font-family: inherit;
  color: ${function (props) {
    return props.$active ? '#000' : '#999';
  }};
  font-weight: ${function (props) {
    return props.$active ? '700' : '400';
  }};
  border-bottom: ${function (props) {
    return props.$active ? '2px solid #000' : '2px solid transparent';
  }};

  &:first-child {
    padding-left: 0;
  }

  &:hover {
    color: #000;
  }
`;

var SortWrapper = styled.div`
  position: relative;
  flex-shrink: 0;
`;

var SortButton = styled.button`
  background: white;
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 0 1.4rem 0 0.6rem;
  height: 28px;
  font-size: 0.72rem;
  font-weight: 600;
  font-family: inherit;
  color: #333;
  cursor: pointer;
  position: relative;
  white-space: nowrap;

  &::after {
    content: '';
    position: absolute;
    right: 0.45rem;
    top: 50%;
    transform: translateY(-50%);
    border-left: 3.5px solid transparent;
    border-right: 3.5px solid transparent;
    border-top: 4px solid #999;
  }

  &:hover {
    border-color: #bbb;
  }
`;

var SortMenu = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  z-index: 9999;
  min-width: 120px;
  overflow: hidden;
`;

var SortOption = styled.button`
  display: block;
  width: 100%;
  background: ${function (props) {
    return props.$active ? '#f5f5f5' : 'white';
  }};
  border: none;
  padding: 0.5rem 0.75rem;
  font-size: 0.75rem;
  font-weight: ${function (props) {
    return props.$active ? '700' : '400';
  }};
  font-family: inherit;
  color: #333;
  cursor: pointer;
  text-align: left;

  &:hover {
    background: #f0f0f0;
  }
`;

var FilterIconButton = styled.button`
  background: white;
  border: 1px solid #ddd;
  border-radius: 6px;
  cursor: pointer;
  padding: 0;
  width: 28px;
  height: 28px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &:hover {
    border-color: #bbb;
  }
`;

var FilterDot = styled.span`
  position: absolute;
  top: 50%;
  right: 2px;
  margin-top: -8px;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #000;
`;

// --- Filter context bar ---

var FilterBar = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0.5rem 1rem;
  margin-bottom: -0.35em;
  font-size: 0.85rem;
  color: #000;
  font-weight: 700;
  border-bottom: 1px solid #eee;
  box-sizing: border-box;
`;

var FilterBarText = styled.span`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

var FilterBarClear = styled.button`
  background: white;
  border: 1px solid #ccc;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.72rem;
  color: #333;
  font-weight: 700;
  padding: 0.2rem 0.5rem;
  flex-shrink: 0;
  margin-left: 0.5rem;
  font-family: inherit;

  &:hover {
    background: #eee;
  }
`;

// --- Bottom sheet ---

var SheetWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 200;
  display: flex;
  align-items: flex-end;
  justify-content: center;
`;

var SheetBackdrop = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  animation: fadeIn 0.2s ease-out;
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

var SheetContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 400px;
  background: white;
  border-radius: 16px 16px 0 0;
  max-height: 65vh;
  overflow-y: auto;
  overscroll-behavior: contain;
  /* The sheet's end clears the player bar, home indicator included. */
  padding: 0 1.25rem calc(1.25rem + 60px + env(safe-area-inset-bottom, 0px));
  -webkit-overflow-scrolling: touch;
  touch-action: pan-y;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
  animation: slideUp 0.25s ease-out;
  @keyframes slideUp {
    from {
      transform: translateY(100%);
    }
    to {
      transform: translateY(0);
    }
  }
`;

var DragHandleArea = styled.div`
  padding: 0.75rem 0 0.5rem;
  cursor: grab;
  touch-action: none;
`;

var DragHandleBar = styled.div`
  width: 36px;
  height: 4px;
  border-radius: 2px;
  background: #ddd;
  margin: 0 auto;
`;

var SheetHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0 0 1rem;
`;

var SheetTitle = styled.h3`
  font-size: 1rem;
  font-weight: 700;
  margin: 0;
`;

var SheetDoneButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 600;
  color: #000;
  padding: 0.25rem 0;
`;

var SheetSectionLabel = styled.p`
  font-size: 0.7rem;
  font-weight: 700;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin: 1rem 0 0.5rem;
`;

var SheetPillGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
`;

var SheetPill = styled.button`
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
  border: 1px solid ${function (props) {
      return props.$selected ? '#000' : '#ddd';
    }};
  background: ${function (props) {
    return props.$selected ? '#000' : 'white';
  }};
  color: ${function (props) {
    return props.$selected ? 'white' : '#333';
  }};
  font-size: 0.75rem;
  font-weight: ${function (props) {
    return props.$selected ? '700' : '400';
  }};
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    border-color: #000;
  }
`;

var SheetResultCount = styled.span`
  font-size: 0.8rem;
  color: #bbb;
  font-weight: 500;
`;

var SheetClearButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 0.85rem;
  color: #999;
  font-weight: 600;
  padding: 0.5rem 0;

  &:hover {
    color: #000;
  }
`;

// --- CTA styled components ---

var ProposalCard = styled.div`
  box-sizing: border-box;
  width: 100%;
  max-width: 400px;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  background: white;
  border: 1px solid #ddd;
  border-right-width: 2px;
  border-bottom-width: 2px;
  border-radius: 12px;
  position: relative;
`;

var ProposalDismiss = styled.button`
  position: absolute;
  top: 0.5rem;
  right: 0.75rem;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1.1rem;
  color: #bbb;
  line-height: 1;
  padding: 0;
  &:hover {
    color: #666;
  }
`;

var ProposalTitle = styled.div`
  font-weight: 800;
  font-size: 1rem;
  margin-bottom: 0.35em;
`;

var ProposalSubtitle = styled.div`
  color: #777;
  font-size: 0.85rem;
  line-height: 1.4;
  margin-bottom: 0.75em;
`;

var ProposalInput = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #999;
  border-radius: 8px;
  padding: 0.6em 0.75em;
  font-size: 0.85rem;
  font-family: inherit;
  resize: vertical;
  min-height: 60px;
  margin-bottom: 0.75em;
  &:focus {
    outline: none;
    border-color: #333;
  }
`;

var ProposalSubmit = styled.button`
  background: black;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 700;
  font-size: 0.85rem;
  font-family: inherit;
  padding: 0.55em 1.5em;
  cursor: pointer;
  &:hover {
    background: #333;
  }
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

var ProposalSuccess = styled.div`
  color: #22c55e;
  font-size: 0.85rem;
  font-weight: 600;
`;

// --- New posts banner ---

var NewPostsBanner = styled.button`
  position: fixed;
  top: 12px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1100;
  background: black;
  color: white;
  border: none;
  border-radius: 20px;
  padding: 0.5em 1.25em;
  font-size: 0.8rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.4em;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  transition: opacity 0.2s ease;
  white-space: nowrap;

  &:hover {
    opacity: 0.85;
  }
`;

// --- Filter icon SVG ---

function FilterIcon(props) {
  var active = props.active;
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke={active ? '#000' : '#999'}
      strokeWidth="2"
      strokeLinecap="round"
    >
      <line x1="4" y1="6" x2="20" y2="6" />
      <line x1="7" y1="12" x2="17" y2="12" />
      <line x1="10" y1="18" x2="14" y2="18" />
    </svg>
  );
}

// ============================================================
// MAIN COMPONENT
// ============================================================

export default function UnifiedFeed() {
  useEffect(function () {
    document.title = 'Feed | HeavyRotation';
  }, []);

  // The prompt card's Respond asks post or list; the card goes away once a
  // choice is made, as it used to on the click.
  var respondToPrompt = usePromptResponseChooser(function (promptId) {
    handleDismissCard('feed_prompt_' + promptId);
  });

  // --- Auth ---
  var authContext = useAuth();
  var authTokens = authContext.authTokens;
  var isLoggedIn = !!authTokens?.token;

  var shareUrl = authTokens?.user?.username
    ? 'https://heavyrotation.app/profile/' + authTokens.user.username
    : null;
  var shareUrlWithRef =
    shareUrl && authTokens?.user?.id ? shareUrl + '?ref=' + authTokens.user.id : shareUrl;

  // ============================================================
  // INLINE INVITE CARD A/B TEST
  // ============================================================

  var HR = (
    <>
      <span style={{ fontWeight: 900, color: '#000' }}>Heavy</span>
      <span style={{ fontWeight: 300, fontStyle: 'italic', color: '#000' }}>Rotation</span>
    </>
  );

  var INVITE_VARIANTS = [
    {
      title: 'Good music travels by word of mouth.',
      subtitle: <>Tell your friends about {HR}.</>,
      buttonText: 'Drop It in a Group Chat',
      sms: true,
    },
    {
      title: <>{HR} is better with people you know.</>,
      subtitle: 'Loop someone in.',
      buttonText: 'Send a Text',
      sms: true,
    },
    {
      title: 'Someone you know needs this.',
      subtitle: <>Let them know about {HR}.</>,
      buttonText: 'Text a Friend',
      sms: true,
    },
    {
      title: 'Music is better with friends',
      subtitle: <>Invite your friends to join {HR}.</>,
      buttonText: 'Invite Friends',
    },
  ];

  var _inviteVariant = useState(function () {
    return Math.floor(Math.random() * INVITE_VARIANTS.length);
  });
  var inviteVariantIdx = _inviteVariant[0];

  var inviteVariant = INVITE_VARIANTS[inviteVariantIdx];
  var inviteVariantTracked = useRef(false);

  function openSms() {
    var smsText =
      'Join me on HeavyRotation and discover new music! ' +
      (shareUrlWithRef || 'https://heavyrotation.app');
    window.location.href = 'sms:?&body=' + encodeURIComponent(smsText);
  }

  var inlineInviteCard = (
    <CTACard
      key="inline-invite"
      title={inviteVariant.title}
      subtitle={inviteVariant.subtitle}
      to={inviteVariant.sms ? undefined : '/invites'}
      onButtonClick={inviteVariant.sms ? openSms : undefined}
      buttonText={inviteVariant.buttonText}
      shareUrl={shareUrl}
      onShareClick={function () {
        trackEvent('inline_invite_share_clicked', {
          variant: inviteVariantIdx,
          title: inviteVariant.title,
        });
      }}
    >
      <div style={{ marginBottom: '0.75em' }}>
        <VinylDisc theme={VINYL_THEMES.test_pressing} size={64} spinning />
      </div>
    </CTACard>
  );

  var inlineSuggestedFriendsCard = (
    <div key="inline-suggested-friends" style={{ maxWidth: 400, margin: '0 auto', width: '100%' }}>
      <SuggestedFriends />
    </div>
  );

  // ============================================================
  // TAB + SORT STATE
  // ============================================================

  // v2 keys reset all users to All / Shuffle defaults
  var _activeTab = useState(function () {
    try {
      return localStorage.getItem('hr_feed_tab_v2') || 'all';
    } catch (e) {
      return 'all';
    }
  });
  var activeTab = _activeTab[0];
  var setActiveTabRaw = _activeTab[1];

  // Force 'all' when not logged in
  var effectiveTab = !isLoggedIn && activeTab === 'following' ? 'all' : activeTab;

  function setActiveTab(tab) {
    setActiveTabRaw(tab);
    try {
      localStorage.setItem('hr_feed_tab_v2', tab);
    } catch (e) {}
  }

  var _sortMode = useState(function () {
    try {
      return localStorage.getItem('hr_feed_sort_v2') || 'recent';
    } catch (e) {
      return 'recent';
    }
  });
  var sortMode = _sortMode[0];
  var setSortModeRaw = _sortMode[1];

  var _randomSeed = useState(function () {
    return Date.now().toString(36);
  });
  var randomSeed = _randomSeed[0];
  var setRandomSeed = _randomSeed[1];

  function setSortMode(mode) {
    setSortModeRaw(mode);
    try {
      localStorage.setItem('hr_feed_sort_v2', mode);
    } catch (e) {}
    if (mode === 'random') {
      setRandomSeed(Date.now().toString(36));
    }
  }

  // ============================================================
  // FILTER STATE (from Explore)
  // ============================================================

  var _genres = useState([]);
  var genres = _genres[0];
  var setGenres = _genres[1];

  var _selectedGenres = useState(function () {
    try {
      var stored = localStorage.getItem('hr_discover_genres');
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      return [];
    }
  });
  var selectedGenres = _selectedGenres[0];
  var setSelectedGenres = _selectedGenres[1];

  var decades = [
    '2020s',
    '2010s',
    '2000s',
    '1990s',
    '1980s',
    '1970s',
    '1960s',
    '1950s',
    '1940s',
    '1930s',
    '1920s',
  ];

  var _selectedDecades = useState(function () {
    try {
      var stored = localStorage.getItem('hr_discover_decades');
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      return [];
    }
  });
  var selectedDecades = _selectedDecades[0];
  var setSelectedDecades = _selectedDecades[1];

  var _sheetOpen = useState(false);
  var sheetOpen = _sheetOpen[0];
  var setSheetOpen = _sheetOpen[1];

  var _sortMenuOpen = useState(false);
  var sortMenuOpen = _sortMenuOpen[0];
  var setSortMenuOpen = _sortMenuOpen[1];
  var sortMenuRef = useRef(null);

  var _dragY = useState(0);
  var dragY = _dragY[0];
  var setDragY = _dragY[1];
  var dragStartRef = useRef(null);
  var dragYRef = useRef(0);
  var sheetRef = useRef(null);

  var hasFilters = selectedGenres.length > 0 || selectedDecades.length > 0;

  // Persist filters
  useEffect(
    function () {
      try {
        localStorage.setItem('hr_discover_genres', JSON.stringify(selectedGenres));
      } catch (e) {}
    },
    [selectedGenres]
  );

  useEffect(
    function () {
      try {
        localStorage.setItem('hr_discover_decades', JSON.stringify(selectedDecades));
      } catch (e) {}
    },
    [selectedDecades]
  );

  // Close sort menu on click outside
  useEffect(
    function () {
      if (!sortMenuOpen) return;
      function handleClick(e) {
        if (sortMenuRef.current && !sortMenuRef.current.contains(e.target)) {
          setSortMenuOpen(false);
        }
      }
      document.addEventListener('mousedown', handleClick);
      return function () {
        document.removeEventListener('mousedown', handleClick);
      };
    },
    [sortMenuOpen]
  );

  // Load genres on mount
  useEffect(function () {
    api
      .get('/api/media/genres')
      .then(function (response) {
        setGenres(response.data || []);
      })
      .catch(function () {});
  }, []);

  function toggleGenre(g) {
    setSelectedGenres(function (prev) {
      return prev.indexOf(g) >= 0
        ? prev.filter(function (x) {
            return x !== g;
          })
        : prev.concat([g]);
    });
  }

  function toggleDecade(d) {
    setSelectedDecades(function (prev) {
      return prev.indexOf(d) >= 0
        ? prev.filter(function (x) {
            return x !== d;
          })
        : prev.concat([d]);
    });
  }

  function decadesToYearPrefixes(decs) {
    return decs.map(function (d) {
      return d.slice(0, 3);
    });
  }

  function clearAllFilters() {
    setSelectedGenres([]);
    setSelectedDecades([]);
  }

  function filterDescription() {
    var parts = [];
    for (var i = 0; i < selectedGenres.length; i++) parts.push(selectedGenres[i]);
    for (var j = 0; j < selectedDecades.length; j++) parts.push(selectedDecades[j]);
    return parts.join(' \u00B7 ');
  }

  // Drag-to-dismiss handlers
  function handleDragMove(clientY) {
    if (dragStartRef.current === null) return;
    var delta = clientY - dragStartRef.current;
    if (delta > 0) {
      dragYRef.current = delta;
      setDragY(delta);
    }
  }

  function handleDragEnd() {
    if (dragYRef.current > 100) setSheetOpen(false);
    dragYRef.current = 0;
    setDragY(0);
    dragStartRef.current = null;
  }

  function onTouchStart(e) {
    dragStartRef.current = e.touches[0].clientY;
  }
  function onTouchMove(e) {
    handleDragMove(e.touches[0].clientY);
  }
  function onTouchEnd() {
    handleDragEnd();
  }

  function onMouseDrag(e) {
    handleDragMove(e.clientY);
  }
  function onMouseUp() {
    window.removeEventListener('mousemove', onMouseDrag);
    window.removeEventListener('mouseup', onMouseUp);
    handleDragEnd();
  }

  // ============================================================
  // CTA STATE (from Feed)
  // ============================================================

  var _liveBadges = useState(authTokens?.user?.badges || []);
  var liveBadges = _liveBadges[0];
  var setLiveBadges = _liveBadges[1];

  var _dismissedCards = useState(null);
  var dismissedCards = _dismissedCards[0];
  var setDismissedCards = _dismissedCards[1];

  // Fetch badges, dismissed cards, active prompt
  useEffect(
    function () {
      if (!isLoggedIn || !authTokens?.user?.username) return;
      api
        .get('/api/users/profile/' + authTokens.user.username)
        .then(function (res) {
          if (res.data && res.data.badges) setLiveBadges(res.data.badges);
          if (res.data && res.data.dismissedCards) {
            setDismissedCards(
              res.data.dismissedCards.split(',').map(function (s) {
                return s.trim();
              })
            );
          } else {
            setDismissedCards([]);
          }
        })
        .catch(function () {});
      api
        .get('/api/prompts/active')
        .then(function (res) {
          setActivePrompts(res.data || []);
        })
        .catch(function () {
          setActivePrompts([]);
        });
      // Whole library, not just the promoted ones: isActive only marks the
      // promotion window, so past prompts are still worth surfacing.
      api
        .get('/api/prompts')
        .then(function (res) {
          setAllPrompts(res.data || []);
        })
        .catch(function () {
          setAllPrompts([]);
        });
      api
        .get('/api/prompts/my-activity')
        .then(function (res) {
          if (res.data && res.data.hasRespondedToAnyPrompt) setHasRespondedToPrompt(true);
          setRespondedPromptIDs((res.data && res.data.respondedPromptIDs) || []);
        })
        .catch(function () {
          setRespondedPromptIDs([]);
        });
    },
    [isLoggedIn, authTokens?.user?.username]
  );

  function isCardDismissed(key) {
    if (dismissedCards === null) return true;
    return dismissedCards.indexOf(key) !== -1;
  }

  var sessionDismissedRef = useRef([]);

  // CTA rotation: cycle through eligible cards across page loads.
  // Guarded because a useRef argument is re-evaluated on every render, which
  // would advance the stored counter once per render instead of once per load.
  var ctaRotationIndex = useRef(null);
  if (ctaRotationIndex.current === null) {
    var storedRotation = parseInt(localStorage.getItem('hr_cta_rotation') || '0', 10);
    localStorage.setItem('hr_cta_rotation', String(storedRotation + 1));
    ctaRotationIndex.current = storedRotation;
  }

  function handleDismissCard(key) {
    sessionDismissedRef.current.push(key);
    setDismissedCards(function (prev) {
      if (prev.indexOf(key) !== -1) return prev;
      return prev.concat([key]);
    });
    api.post('/api/users/dismiss-card', { key: key }).catch(function () {});
  }

  // null until the fetch settles, so the fallback does not pick from a partial pool
  var _activePrompts = useState(null);
  var activePrompts = _activePrompts[0];
  var setActivePrompts = _activePrompts[1];

  var _allPrompts = useState(null);
  var allPrompts = _allPrompts[0];
  var setAllPrompts = _allPrompts[1];

  var _respondedPromptIDs = useState(null);
  var respondedPromptIDs = _respondedPromptIDs[0];
  var setRespondedPromptIDs = _respondedPromptIDs[1];

  // Fixed once per mount so the fallback prompt does not change between renders
  var promptShuffleSeed = useRef(Math.random());

  var _hasRespondedToPrompt = useState(false);
  var hasRespondedToPrompt = _hasRespondedToPrompt[0];
  var setHasRespondedToPrompt = _hasRespondedToPrompt[1];

  var _proposalText = useState('');
  var proposalText = _proposalText[0];
  var setProposalText = _proposalText[1];

  var _proposalSent = useState(false);
  var proposalSent = _proposalSent[0];
  var setProposalSent = _proposalSent[1];

  var _proposalSending = useState(false);
  var proposalSending = _proposalSending[0];
  var setProposalSending = _proposalSending[1];

  var _proposalError = useState(false);
  var proposalError = _proposalError[0];
  var setProposalError = _proposalError[1];

  function badgeCTA(badgeKey, subtitle) {
    var config = BADGE_CONFIG[badgeKey];
    if (!config) return null;
    if (liveBadges.indexOf(badgeKey) === -1) return null;
    var dismissKey = badgeKey + '_milestone';
    if (isCardDismissed(dismissKey)) return null;
    return (
      <CTACard
        key={dismissKey}
        title={config.label}
        subtitle={subtitle}
        to={'/profile/' + authTokens.user.username}
        buttonText="View on Profile"
        dismissKey={dismissKey}
        onDismiss={handleDismissCard}
      >
        <div style={{ marginBottom: '0.75em' }}>
          <BadgeIcon badgeKey={badgeKey} size={64} />
        </div>
      </CTACard>
    );
  }

  function promptCTA(prompt) {
    if (!prompt) return null;
    var dismissKey = 'feed_prompt_' + prompt.id;
    if (isCardDismissed(dismissKey)) return null;
    return (
      <CTACard
        key={'prompt-cta-' + prompt.id}
        title={prompt.text}
        subtitle={
          prompt.responseCount > 0
            ? prompt.responseCount + (prompt.responseCount === 1 ? ' response' : ' responses')
            : 'Be the first to respond!'
        }
        onButtonClick={function (e) {
          respondToPrompt.open(e, prompt.id);
        }}
        buttonProps={{
          'aria-haspopup': 'menu',
          'aria-expanded': respondToPrompt.triggerProps(prompt.id)['aria-expanded'],
        }}
        analyticsDestination="prompt_response_chooser"
        buttonText="Respond"
        secondaryTo={'/prompts/' + prompt.id}
        secondaryText="See Responses"
        dismissKey={dismissKey}
        dismissed={false}
        onDismiss={handleDismissCard}
      />
    );
  }

  // Fallback pool: any prompt the user has not dismissed, promoted or not.
  // Prefers ones they have not answered, but re-showing an answered prompt
  // beats an empty slot. Waits for every fetch so the pick is made once,
  // against a settled pool.
  function randomPromptCTA() {
    if (dismissedCards === null || allPrompts === null || respondedPromptIDs === null) {
      return null;
    }
    var pool = allPrompts.filter(function (p) {
      return !isCardDismissed('feed_prompt_' + p.id);
    });
    var unanswered = pool.filter(function (p) {
      return respondedPromptIDs.indexOf(p.id) === -1;
    });
    if (unanswered.length > 0) pool = unanswered;
    if (pool.length === 0) return null;
    return promptCTA(pool[Math.floor(promptShuffleSeed.current * pool.length)]);
  }

  var milestoneCTAs = [
    badgeCTA(
      'demo_tape',
      "You're one of the first 100 listeners on HeavyRotation. Thanks for being here!"
    ),
    badgeCTA('test_pressing', "You're one of the first 250 listeners on HeavyRotation."),
    badgeCTA('white_label', "You're one of the first 500 listeners on HeavyRotation."),
    badgeCTA('certified_gold', "You're one of the first 1,000 listeners on HeavyRotation."),
    badgeCTA('platinum_record', "You're one of the first 2,500 listeners on HeavyRotation."),
    badgeCTA('fan_mail', 'A friend of yours joined HeavyRotation. Thanks for spreading the word!'),
    badgeCTA('fan_club', "You've referred 10 friends to HeavyRotation. Welcome to the fan club!"),
  ].filter(Boolean);

  var allCTAs = [
    // 0. Guide (first-time users)
    !isCardDismissed('feed_guide') && (
      <CTACard
        key="guide-cta"
        dismissKey="feed_guide"
        dismissed={false}
        onDismiss={handleDismissCard}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            width: '100%',
            textAlign: 'left',
          }}
        >
          <VinylDisc theme={VINYL_THEMES.test_pressing} size={48} spinning />
          <div>
            <div style={{ fontWeight: 800, fontSize: '1rem', marginBottom: '0.25em' }}>
              How HeavyRotation Works
            </div>
            <div style={{ color: '#777', fontSize: '0.85rem', lineHeight: 1.4 }}>
              Getting the most out of your feed, discovering new music, and more.
            </div>
            <a
              href="/guide"
              style={{
                display: 'inline-block',
                marginTop: '0.5em',
                background: 'black',
                color: 'white',
                borderRadius: '8px',
                padding: '0.3em 0.85em',
                fontSize: '0.75rem',
                fontWeight: 700,
                textDecoration: 'none',
              }}
            >
              Read the Guide &rarr;
            </a>
          </div>
        </div>
      </CTACard>
    ),
    // 1. Badge milestones (highest priority)
    ...milestoneCTAs,
    // 2. Invite friends
    !isCardDismissed('feed_invite_v3') && (
      <CTACard
        key="invite-cta"
        title="Music is better with friends"
        subtitle="Invite your friends to join HeavyRotation."
        to="/invites"
        buttonText="Invite Friends"
        shareUrl={shareUrl}
        dismissKey="feed_invite_v3"
        dismissed={false}
        onDismiss={handleDismissCard}
      >
        <div style={{ marginBottom: '0.75em' }}>
          <VinylDisc theme={VINYL_THEMES.test_pressing} size={64} spinning />
        </div>
      </CTACard>
    ),
    // The newest prompt is not in the rotation: it leads on its own once
    // (the top slot, below), and after that the shuffled prompts carry it.
    // 4. Prompt proposal
    hasRespondedToPrompt && !isCardDismissed('feed_propose_prompt') && (
      <ProposalCard key="propose-prompt-cta">
        <ProposalDismiss
          onClick={function () {
            handleDismissCard('feed_propose_prompt');
          }}
          aria-label="Dismiss"
        >
          &times;
        </ProposalDismiss>
        <ProposalTitle>Thanks for responding to a prompt!</ProposalTitle>
        {proposalSent ? (
          <ProposalSuccess>Sent! Thanks for the idea.</ProposalSuccess>
        ) : (
          <>
            <ProposalSubtitle>Have an idea for the next one?</ProposalSubtitle>
            {proposalError && (
              <div style={{ color: 'tomato', fontSize: '0.8rem', marginBottom: '0.5em' }}>
                Something went wrong. Try again?
              </div>
            )}
            <ProposalInput
              placeholder="Your prompt idea..."
              value={proposalText}
              onChange={function (e) {
                setProposalText(e.target.value);
                setProposalError(false);
              }}
            />
            <ProposalSubmit
              disabled={!proposalText.trim() || proposalSending}
              onClick={function () {
                setProposalSending(true);
                api
                  .post('/api/feedback', { message: '[Prompt proposal] ' + proposalText.trim() })
                  .then(function () {
                    setProposalSent(true);
                    setProposalText('');
                    setTimeout(function () {
                      handleDismissCard('feed_propose_prompt');
                    }, 5000);
                  })
                  .catch(function () {
                    setProposalError(true);
                  })
                  .finally(function () {
                    setProposalSending(false);
                  });
              }}
            >
              {proposalSending ? 'Sending...' : 'Propose a Prompt'}
            </ProposalSubmit>
          </>
        )}
      </ProposalCard>
    ),
    // 5. Play feed
    !isCardDismissed('feed_play_all') && (
      <CTACard
        key="play-all-cta"
        dismissKey="feed_play_all"
        dismissed={false}
        onDismiss={handleDismissCard}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            width: '100%',
            textAlign: 'left',
          }}
        >
          <VinylDisc theme={VINYL_THEMES.test_pressing} size={48} spinning />
          <div>
            <div style={{ fontWeight: 800, fontSize: '1rem', marginBottom: '0.25em' }}>
              Your feed is a playlist now.
            </div>
            <div style={{ color: '#777', fontSize: '0.85rem', lineHeight: 1.4 }}>
              Hit play at the top and listen to your feed. Also available on your{' '}
              <a
                href="/bookmarks"
                style={{ color: '#555', textDecoration: 'none', fontWeight: 600 }}
              >
                Bookmarks
              </a>
              .
            </div>
          </div>
        </div>
      </CTACard>
    ),
    // 6. Streaming services
    !isCardDismissed('feed_streaming_services') && (
      <CTACard
        key="streaming-services-cta"
        title="Open music directly in Tidal, Deezer, Apple Music, and more."
        subtitle="Set up your streaming preferences and we'll link you straight to the music."
        to="/streaming"
        buttonText="Streaming Preferences"
        dismissKey="feed_streaming_services"
        dismissed={false}
        onDismiss={handleDismissCard}
      />
    ),
    // 6. Music search
    !isCardDismissed('feed_music_search') && (
      <CTACard
        key="music-search-cta"
        title="Search Artists, Albums, and Songs"
        subtitle="Discover new music and see community posts."
        to="/music/search"
        buttonText="Search Music"
        dismissKey="feed_music_search"
        dismissed={false}
        onDismiss={handleDismissCard}
      />
    ),
    // 7. Instagram
    !isCardDismissed('feed_instagram') && (
      <CTACard
        key="instagram-cta"
        href="https://www.instagram.com/heavyrotation.app"
        buttonText="Follow @heavyrotation.app"
        dismissKey="feed_instagram"
        dismissed={false}
        onDismiss={handleDismissCard}
      >
        <div style={{ marginBottom: '0.75em' }}>
          <VinylDisc theme={VINYL_THEMES.test_pressing} size={64} spinning />
        </div>
        <div style={{ fontSize: '1rem', marginBottom: '0.35em' }}>
          <HeavyText>Heavy</HeavyText>
          <LightItalicText>Rotation</LightItalicText>
          <span style={{ fontWeight: 400 }}> is on Instagram</span>
        </div>
      </CTACard>
    ),
  ].filter(Boolean);

  // The top slot, chosen once per visit when the dismissals and prompts have
  // loaded, so it never switches cards under the reader:
  //   1. the newest promoted prompt, on the first visit after it goes up
  //      (unless it was already answered or dismissed);
  //   2. the Lists announcement, until it is dismissed or used;
  //   3. otherwise the rotation below, falling back to a shuffled prompt.
  var topPick = useRef(null);
  // A visit is the browser session, not one mount of the feed: opening a
  // post and coming back keeps the new prompt on top.
  function leadPromptThisSession() {
    try {
      return sessionStorage.getItem('hr_lead_prompt');
    } catch (e) {
      return null;
    }
  }
  if (
    topPick.current === null &&
    dismissedCards !== null &&
    activePrompts !== null &&
    respondedPromptIDs !== null
  ) {
    var newestPrompt = activePrompts[0];
    if (
      newestPrompt &&
      !isCardDismissed('feed_prompt_' + newestPrompt.id) &&
      (!isCardDismissed('feed_seen_prompt_' + newestPrompt.id) ||
        leadPromptThisSession() === newestPrompt.id) &&
      respondedPromptIDs.indexOf(newestPrompt.id) === -1
    ) {
      topPick.current = { kind: 'prompt', prompt: newestPrompt };
    } else if (!isCardDismissed(LISTS_CTA_KEY)) {
      topPick.current = { kind: 'lists' };
    } else {
      topPick.current = { kind: 'rotation' };
    }
  }

  // A new prompt leads only once: recorded as seen for the next visit, but
  // not in this visit's state, so it stays up until the reader leaves.
  var seenPromptMarked = useRef(false);
  useEffect(function () {
    var pick = topPick.current;
    if (!pick || pick.kind !== 'prompt' || seenPromptMarked.current) return;
    seenPromptMarked.current = true;
    try {
      sessionStorage.setItem('hr_lead_prompt', pick.prompt.id);
    } catch (e) {}
    api
      .post('/api/users/dismiss-card', { key: 'feed_seen_prompt_' + pick.prompt.id })
      .catch(function () {});
  });

  if (sessionDismissedRef.current.length > 0 || topPick.current === null) {
    allCTAs = [];
  } else if (topPick.current.kind === 'prompt') {
    allCTAs = [promptCTA(topPick.current.prompt)].filter(Boolean);
  } else if (topPick.current.kind === 'lists') {
    allCTAs = [
      <CTACard
        key="lists-cta"
        title="New: 3x3"
        subtitle="Pick nine songs or albums that say who you are."
        to="/create-list?style=grid"
        buttonText="Make a 3x3"
        dismissKey={LISTS_CTA_KEY}
        dismissed={false}
        dismissOnClick
        onDismiss={handleDismissCard}
      >
        <div style={{ marginBottom: '0.75em' }}>
          <VinylDisc theme={VINYL_THEMES.test_pressing} size={64} spinning />
        </div>
      </CTACard>,
    ];
  } else if (allCTAs.length > 0) {
    var ctaIdx = ctaRotationIndex.current % allCTAs.length;
    allCTAs = [allCTAs[ctaIdx]].filter(Boolean);
  } else {
    // Nothing queued (and nothing dismissed this session): show a random active prompt
    allCTAs = [randomPromptCTA()].filter(Boolean);
  }

  // ============================================================
  // DATA FETCHING
  // ============================================================

  // --- "All" tab: uses /api/posts/search. Skips fetch when Following tab is active. ---
  var fetchAllPage = useCallback(
    function (cursor) {
      if (effectiveTab !== 'all')
        return Promise.resolve({ posts: [], hasMore: false, nextCursor: null });
      var params = { limit: 20 };
      if (selectedGenres.length > 0) params.genre = selectedGenres.join(',');
      if (selectedDecades.length > 0)
        params.year = decadesToYearPrefixes(selectedDecades).join(',');
      if (sortMode === 'trending') {
        params.mode = 'trending';
        if (cursor) params.page = cursor;
      } else if (sortMode === 'random') {
        params.mode = 'random';
        params.seed = randomSeed;
        if (cursor) params.page = cursor;
      } else {
        params.mode = 'recent';
        if (cursor) params.before = cursor;
      }
      return api.get('/api/posts/search', { params: params }).then(function (response) {
        var data = response.data;
        return { posts: data.posts || [], hasMore: data.hasMore, nextCursor: data.nextCursor };
      });
    },
    [effectiveTab, sortMode, selectedGenres, selectedDecades, randomSeed]
  );

  var allScroll = useInfiniteScroll(fetchAllPage, [
    effectiveTab,
    sortMode,
    selectedGenres,
    selectedDecades,
    randomSeed,
  ]);

  // --- "Following" tab ---
  // Recent + no filters: use /api/feed?section=followed (includes reposts)
  // Otherwise: use /api/posts/search?scope=followed
  var _followingRefreshKey = useState(0);
  var followingRefreshKey = _followingRefreshKey[0];
  var setFollowingRefreshKey = _followingRefreshKey[1];

  var fetchFollowingPage = useCallback(
    function (cursor) {
      if (effectiveTab !== 'following')
        return Promise.resolve({ posts: [], hasMore: false, nextCursor: null });
      var filtersActive = selectedGenres.length > 0 || selectedDecades.length > 0;
      if (sortMode === 'recent' && !filtersActive) {
        var url = '/api/feed?section=followed&limit=20';
        if (cursor) url += '&followedBefore=' + encodeURIComponent(cursor);
        return api.get(url).then(function (response) {
          var data = response.data;
          return {
            posts: data.followed || [],
            hasMore: data.hasMoreFollowed,
            nextCursor: data.nextFollowedCursor,
          };
        });
      }
      var params = { limit: 20, scope: 'followed', mode: sortMode };
      if (selectedGenres.length > 0) params.genre = selectedGenres.join(',');
      if (selectedDecades.length > 0)
        params.year = decadesToYearPrefixes(selectedDecades).join(',');
      if (sortMode === 'random') params.seed = randomSeed;
      if (cursor) {
        if (sortMode === 'trending' || sortMode === 'random') params.page = cursor;
        else params.before = cursor;
      }
      return api.get('/api/posts/search', { params: params }).then(function (response) {
        var data = response.data;
        return { posts: data.posts || [], hasMore: data.hasMore, nextCursor: data.nextCursor };
      });
    },
    [effectiveTab, sortMode, selectedGenres, selectedDecades, randomSeed, followingRefreshKey]
  );

  var followingScroll = useInfiniteScroll(fetchFollowingPage, [
    effectiveTab,
    sortMode,
    selectedGenres,
    selectedDecades,
    randomSeed,
    followingRefreshKey,
  ]);

  // Active scroll
  var scroll = effectiveTab === 'following' ? followingScroll : allScroll;

  // ============================================================
  // INTERACTION MAPS
  // ============================================================

  var _bookmarkedMap = useState({});
  var bookmarkedMap = _bookmarkedMap[0];
  var setBookmarkedMap = _bookmarkedMap[1];

  var _libraryAddMap = useState({});
  var libraryAddMap = _libraryAddMap[0];
  var setLibraryAddMap = _libraryAddMap[1];

  var _followedMap = useState({});
  var followedMap = _followedMap[0];
  var setFollowedMap = _followedMap[1];

  var _userReactionsMap = useState({});
  var userReactionsMap = _userReactionsMap[0];
  var setUserReactionsMap = _userReactionsMap[1];

  var _likedCommentsMap = useState({});
  var likedCommentsMap = _likedCommentsMap[0];
  var setLikedCommentsMap = _likedCommentsMap[1];

  var _repostedMap = useState({});
  var repostedMap = _repostedMap[0];
  var setRepostedMap = _repostedMap[1];

  useEffect(
    function () {
      if (!isLoggedIn) return;
      api
        .get('/api/bookmarks')
        .then(function (r) {
          var map = {};
          var ids = r.data || [];
          for (var i = 0; i < ids.length; i++) map[ids[i]] = true;
          setBookmarkedMap(map);
        })
        .catch(function () {});
      api
        .get('/api/library-adds')
        .then(function (r) {
          var map = {};
          var ids = r.data || [];
          for (var i = 0; i < ids.length; i++) map[ids[i]] = true;
          setLibraryAddMap(map);
        })
        .catch(function () {});
      api
        .get('/api/follows')
        .then(function (r) {
          var map = {};
          var ids = r.data || [];
          for (var i = 0; i < ids.length; i++) map[ids[i]] = true;
          setFollowedMap(map);
        })
        .catch(function () {});
      api
        .get('/api/post-reactions')
        .then(function (r) {
          var map = {};
          var items = r.data || [];
          for (var i = 0; i < items.length; i++) {
            var pid = items[i].postId;
            if (!map[pid]) map[pid] = [];
            map[pid].push(items[i].emoji);
          }
          setUserReactionsMap(map);
        })
        .catch(function () {});
      api
        .get('/api/comment-likes')
        .then(function (r) {
          var map = {};
          var ids = r.data || [];
          for (var i = 0; i < ids.length; i++) map[ids[i]] = true;
          setLikedCommentsMap(map);
        })
        .catch(function () {});
      api
        .get('/api/reposts')
        .then(function (r) {
          var map = {};
          var ids = r.data || [];
          for (var i = 0; i < ids.length; i++) map[ids[i]] = true;
          setRepostedMap(map);
        })
        .catch(function () {});
    },
    [isLoggedIn]
  );

  function handleBookmarkToggle(postId, bookmarked) {
    setBookmarkedMap(function (prev) {
      var next = {};
      for (var k in prev) {
        if (prev.hasOwnProperty(k)) next[k] = prev[k];
      }
      next[postId] = bookmarked;
      return next;
    });
  }

  function handleReactionToggle(postId, emoji, reacted) {
    setUserReactionsMap(function (prev) {
      var next = {};
      for (var k in prev) {
        if (prev.hasOwnProperty(k)) next[k] = (prev[k] || []).slice();
      }
      if (!next[postId]) next[postId] = [];
      if (reacted) {
        if (next[postId].indexOf(emoji) === -1) next[postId].push(emoji);
      } else {
        next[postId] = next[postId].filter(function (e) {
          return e !== emoji;
        });
      }
      return next;
    });
  }

  function handleFollowToggle(userId, following) {
    setFollowedMap(function (prev) {
      var next = {};
      for (var k in prev) {
        if (prev.hasOwnProperty(k)) next[k] = prev[k];
      }
      next[userId] = following;
      return next;
    });
  }

  function handleCommentLikeToggle(commentId, liked) {
    setLikedCommentsMap(function (prev) {
      var next = {};
      for (var k in prev) {
        if (prev.hasOwnProperty(k)) next[k] = prev[k];
      }
      next[commentId] = liked;
      return next;
    });
  }

  function handleRepostToggle(postId, reposted) {
    setRepostedMap(function (prev) {
      var next = {};
      for (var k in prev) {
        if (prev.hasOwnProperty(k)) next[k] = prev[k];
      }
      next[postId] = reposted;
      return next;
    });
    if (reposted) newestLoadedTimestamp.current = new Date().toISOString();
  }

  function handlePostDelete(postId) {
    allScroll.removePost(postId);
    followingScroll.removePost(postId);
  }

  // ============================================================
  // NEW POSTS BANNER (Following + Recent + no filters only)
  // ============================================================

  var _showNewPostsBanner = useState(false);
  var showNewPostsBanner = _showNewPostsBanner[0];
  var setShowNewPostsBanner = _showNewPostsBanner[1];

  var newestLoadedTimestamp = useRef(null);
  var lastCheckTime = useRef(0);

  var showBannerConditions =
    isLoggedIn && effectiveTab === 'following' && sortMode === 'recent' && !hasFilters;

  // Track the newest post timestamp
  useEffect(
    function () {
      if (!showBannerConditions) return;
      var posts = followingScroll.posts;
      if (posts.length > 0) {
        var first = posts[0];
        var effectiveDate = first.sharedAt || first.created_at;
        if (effectiveDate) newestLoadedTimestamp.current = effectiveDate;
      }
    },
    [followingScroll.posts, showBannerConditions]
  );

  var checkForNewPosts = useCallback(
    function () {
      if (!showBannerConditions || followingScroll.loading) return;
      var now = Date.now();
      if (now - lastCheckTime.current < 60000) return;
      lastCheckTime.current = now;
      api
        .get('/api/feed/latest-timestamp')
        .then(function (res) {
          var latest = res.data.latestCreatedAt;
          if (
            latest &&
            newestLoadedTimestamp.current &&
            new Date(latest) > new Date(newestLoadedTimestamp.current)
          ) {
            setShowNewPostsBanner(true);
          }
        })
        .catch(function () {});
    },
    [showBannerConditions, followingScroll.loading]
  );

  // Check on tab focus
  useEffect(
    function () {
      if (!showBannerConditions) return;
      function onVisibilityChange() {
        if (document.visibilityState === 'visible') checkForNewPosts();
      }
      document.addEventListener('visibilitychange', onVisibilityChange);
      return function () {
        document.removeEventListener('visibilitychange', onVisibilityChange);
      };
    },
    [showBannerConditions, checkForNewPosts]
  );

  // Poll every 60s
  useEffect(
    function () {
      if (!showBannerConditions) return;
      var interval = setInterval(checkForNewPosts, 60000);
      return function () {
        clearInterval(interval);
      };
    },
    [showBannerConditions, checkForNewPosts]
  );

  // Check on scroll to top
  useEffect(
    function () {
      if (!showBannerConditions) return;
      function onScroll() {
        if (window.scrollY < 50 && followingScroll.posts.length > 0) checkForNewPosts();
      }
      window.addEventListener('scroll', onScroll, { passive: true });
      return function () {
        window.removeEventListener('scroll', onScroll);
      };
    },
    [showBannerConditions, checkForNewPosts, followingScroll.posts.length]
  );

  // Hide banner when leaving Following + Recent
  useEffect(
    function () {
      if (!showBannerConditions) setShowNewPostsBanner(false);
    },
    [showBannerConditions]
  );

  // ============================================================
  // RENDER
  // ============================================================

  function renderPost(post) {
    return (
      <DisplayPost
        post={post}
        key={post.repostId || post.id}
        isBookmarked={!!bookmarkedMap[post.id]}
        userReactions={userReactionsMap[post.id] || []}
        isAddedToLibrary={!!libraryAddMap[post.id]}
        isFollowing={!!followedMap[post.user.id]}
        isReposted={!!repostedMap[post.id]}
        onBookmarkToggle={handleBookmarkToggle}
        onReactionToggle={handleReactionToggle}
        onFollowToggle={handleFollowToggle}
        followSource={'feed_' + effectiveTab}
        likedCommentsMap={likedCommentsMap}
        onCommentLikeToggle={handleCommentLikeToggle}
        onPostDelete={handlePostDelete}
        onRepostToggle={handleRepostToggle}
      />
    );
  }

  var posts = scroll.posts;
  var loading = scroll.loading;
  var loadingMore = scroll.loadingMore;
  var sentinelRef = scroll.sentinelRef;

  // The invite card's impression, once per mount, only when the list is long
  // enough for the card to be placed (after the 12th post).
  var inviteCardPlaced = isLoggedIn && posts.filter(isRenderableInFeed).length > 12;
  useEffect(
    function () {
      if (!inviteCardPlaced || inviteVariantTracked.current) return;
      inviteVariantTracked.current = true;
      trackEvent('inline_invite_shown', { variant: inviteVariantIdx, title: inviteVariant.title });
    },
    [inviteCardPlaced, inviteVariantIdx, inviteVariant.title]
  );

  return (
    <>
      <FeedContainer>
        {/* CTA cards (logged-in only) */}
        {isLoggedIn && allCTAs}

        {/* Tab row + sort + filter */}
        <TabRow>
          <TabList>
            <Tab
              $active={activeTab === 'all'}
              onClick={function () {
                setActiveTab('all');
              }}
            >
              All
            </Tab>
            {isLoggedIn && (
              <Tab
                $active={activeTab === 'following'}
                onClick={function () {
                  setActiveTab('following');
                }}
              >
                Following
              </Tab>
            )}
          </TabList>
          <SortWrapper ref={sortMenuRef}>
            <SortButton
              onClick={function () {
                setSortMenuOpen(!sortMenuOpen);
              }}
            >
              {sortMode === 'trending' ? 'Trending' : sortMode === 'recent' ? 'Recent' : 'Shuffle'}
            </SortButton>
            {sortMenuOpen && (
              <SortMenu>
                {['trending', 'recent', 'random'].map(function (mode) {
                  var label =
                    mode === 'trending' ? 'Trending' : mode === 'recent' ? 'Recent' : 'Shuffle';
                  return (
                    <SortOption
                      key={mode}
                      $active={sortMode === mode}
                      onClick={function () {
                        setSortMode(mode);
                        setSortMenuOpen(false);
                      }}
                    >
                      {label}
                    </SortOption>
                  );
                })}
              </SortMenu>
            )}
          </SortWrapper>
          <FilterIconButton
            onClick={function () {
              setSheetOpen(true);
            }}
          >
            <FilterIcon active={hasFilters} />
            {hasFilters && <FilterDot />}
          </FilterIconButton>
          <PlayAllButton
            posts={scroll.posts}
            loading={scroll.loading}
            source={'feed_' + effectiveTab + '_' + sortMode}
            dismissedCards={dismissedCards || []}
          />
        </TabRow>

        {/* Active filter context bar */}
        {hasFilters && (
          <FilterBar>
            <FilterBarText>Filtered by: {filterDescription()}</FilterBarText>
            <FilterBarClear onClick={clearAllFilters}>Clear</FilterBarClear>
          </FilterBar>
        )}

        {/* New posts banner */}
        {showNewPostsBanner && showBannerConditions && (
          <NewPostsBanner
            onClick={function () {
              setShowNewPostsBanner(false);
              newestLoadedTimestamp.current = new Date().toISOString();
              window.scrollTo({ top: 0, behavior: 'smooth' });
              setFollowingRefreshKey(function (k) {
                return k + 1;
              });
            }}
          >
            <span style={{ fontSize: '0.9em' }}>&#8593;</span> New posts
          </NewPostsBanner>
        )}

        {/* Posts */}
        {loading ? (
          <LoadingText>Loading...</LoadingText>
        ) : posts.length === 0 ? (
          <EmptyMessage>
            {hasFilters
              ? 'No posts match your filters'
              : effectiveTab === 'following'
                ? 'Follow people to see their posts here'
                : 'Nothing here yet. Check back soon!'}
          </EmptyMessage>
        ) : (
          <>
            {(function () {
              var items = posts.filter(isRenderableInFeed).map(renderPost);
              // The invite goes in first so it lands after the 12th post
              // even once Suggested Friends is placed after the 5th.
              if (isLoggedIn && items.length > 12) {
                items.splice(12, 0, inlineInviteCard);
              }
              if (isLoggedIn && items.length > 5) {
                items.splice(5, 0, inlineSuggestedFriendsCard);
              }
              return items;
            })()}
            <div ref={sentinelRef} />
            {loadingMore && <LoadingText>Loading more...</LoadingText>}
          </>
        )}
      </FeedContainer>

      {/* Filter bottom sheet */}
      {sheetOpen && (
        <SheetWrapper>
          <SheetBackdrop
            onClick={function () {
              setSheetOpen(false);
            }}
            style={{ opacity: dragY > 0 ? Math.max(0, 1 - dragY / 300) : 1 }}
          />
          <SheetContainer
            ref={sheetRef}
            style={{ transform: dragY > 0 ? 'translateY(' + dragY + 'px)' : 'none' }}
          >
            <DragHandleArea
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
              onMouseDown={function (e) {
                dragStartRef.current = e.clientY;
                window.addEventListener('mousemove', onMouseDrag);
                window.addEventListener('mouseup', onMouseUp);
              }}
            >
              <DragHandleBar />
            </DragHandleArea>
            <SheetHeader>
              <SheetTitle>
                Filter{' '}
                <SheetResultCount>
                  {loading ? '' : posts.length + (scroll.hasMore ? '+' : '') + ' posts'}
                </SheetResultCount>
              </SheetTitle>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                {hasFilters && <SheetClearButton onClick={clearAllFilters}>Clear</SheetClearButton>}
                <SheetDoneButton
                  onClick={function () {
                    setSheetOpen(false);
                  }}
                >
                  Done
                </SheetDoneButton>
              </div>
            </SheetHeader>

            <SheetSectionLabel>Genre</SheetSectionLabel>
            <SheetPillGrid>
              <SheetPill
                $selected={selectedGenres.length === 0}
                onClick={function () {
                  setSelectedGenres([]);
                }}
              >
                All
              </SheetPill>
              {genres.map(function (g) {
                return (
                  <SheetPill
                    key={g}
                    $selected={selectedGenres.indexOf(g) >= 0}
                    onClick={function () {
                      toggleGenre(g);
                    }}
                  >
                    {g}
                  </SheetPill>
                );
              })}
            </SheetPillGrid>

            <SheetSectionLabel>Year</SheetSectionLabel>
            <SheetPillGrid>
              <SheetPill
                $selected={selectedDecades.length === 0}
                onClick={function () {
                  setSelectedDecades([]);
                }}
              >
                All
              </SheetPill>
              {decades.map(function (d) {
                return (
                  <SheetPill
                    key={d}
                    $selected={selectedDecades.indexOf(d) >= 0}
                    onClick={function () {
                      toggleDecade(d);
                    }}
                  >
                    {d}
                  </SheetPill>
                );
              })}
            </SheetPillGrid>
          </SheetContainer>
        </SheetWrapper>
      )}
      {respondToPrompt.chooser}
    </>
  );
}
